import React from "react";
import styles from "./List.module.scss";
import type { CardProps } from "../Card";
import Card, { filterCardProps } from "../Card";
import clsx from "clsx";
import { CheckBox } from "../form";
import { createPropsFilter } from "../../utils/props";

export type TValidListValues =
  | string
  | number
  | symbol
  | null
  | undefined
  | object;

export type TItem<T extends TValidListValues> = {
  label: string;
  value: T;
};

export type ListProps<T extends TValidListValues> = CardProps & {
  items: TItem<T>[];
} & (
    | {
        multiple: true;
        value?: T[];
        onChange?: (value: T[]) => void;
      }
    | {
        multiple?: false;
        value?: T;
        onChange?: (value: T) => void;
      }
  );

export function filterListProps<
  P extends TValidListValues,
  T extends ListProps<P>,
>(props: T) {
  return createPropsFilter<ListProps<P>>([
    "borderColor",
    "borderRadius",
    "borderThickness",
    "borderType",
    "children",
    "className",
    "color",
    "elevation",
    "height",
    "items",
    "loading",
    "maxHeight",
    "maxWidth",
    "minHeight",
    "minWidth",
    "onChange",
    "onClick",
    "style",
    "tag",
    "value",
    "width",
    "multiple",
  ])(props);
}

function List<T extends TValidListValues>(props: ListProps<T>) {
  const [cardProps, listProps] = filterCardProps(props);
  const { items, onChange, value, multiple } = listProps;
  const isActive = (v: T | T[]) =>
    Array.isArray(value) ? value.includes(v as any) : value == v;
  return (
    <Card
      {...cardProps}
      className={clsx(
        styles["wrapper"],
        multiple && styles["wrapper--multiple"],
        cardProps.className,
      )}
    >
      <ul className={styles["wrapper__list"]}>
        {items.map((item) => (
          <li
            className={clsx(
              styles["wrapper__list__item"],
              isActive(item.value) && styles["wrapper__list__item--active"],
            )}
            onClick={() => {
              if (item.value === undefined || item.value === null) return;
              if (multiple === true) {
                let newValue: T[];
                if (!Array.isArray(value) && value) newValue = [value];
                else newValue = [...((value || []) as any)];
                if (newValue.includes(item.value)) {
                  newValue.splice(newValue.indexOf(item.value), 1);
                } else {
                  newValue.push(item.value);
                }
                onChange?.(newValue as T[] & T);
              } else {
                onChange?.(item.value as T[] & T);
              }
            }}
          >
            <div
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              {multiple ? (
                <CheckBox
                  checked={isActive(item.value)}
                  id={item.value as string}
                  label={item.label}
                  readOnly
                />
              ) : (
                item.label
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default React.memo(List);
